import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PowerballBall } from "./PowerballBall";
import { Trash2, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAccess } from "@/context/AccessContext";
import { ResultAlerts } from "./ResultAlerts";
import { StreakStats } from "./StreakStats";
import { ReferralCard } from "./ReferralCard";
import { Link } from "react-router-dom";
import { Printer } from "lucide-react";
import { toast } from "sonner";

export function SavedPicks() {
  const { user } = useAuth();
  const { active } = useAccess();
  const [picks, setPicks] = useState([]);
  const [cmp, setCmp] = useState(null);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!active) return;
    setLoading(true);
    Promise.all([api.get("/picks"), api.get("/alerts/latest").catch(() => null)])
      .then(([r, c]) => { setPicks(r.data.picks); setCmp(c?.data || null); setVersion((v) => v + 1); })
      .finally(() => setLoading(false));
  }, [active]);
  const resultFor = (id) => cmp?.results.find((r) => r.pick_id === id);

  // reload whenever the identity (signed-in user) or access changes
  useEffect(() => { load(); }, [load, user]);
  useEffect(() => {
    window.addEventListener("pb:picks-changed", load);
    return () => window.removeEventListener("pb:picks-changed", load);
  }, [load]);

  const del = async (id) => {
    try {
      await api.delete(`/picks/${id}`);
      setPicks((p) => p.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed");
    }
  };

  if (!active) return null;

  return (
    <section id="picks" className="px-4 sm:px-8 lg:px-12 py-12 max-w-[1400px] mx-auto">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="label-caps text-oracle">Vault</div>
          <h2 className="font-heading text-3xl sm:text-4xl mt-1 tracking-tight text-parchment">
            My <span className="italic text-oracle">Saved Picks</span>
          </h2>
        </div>
        {picks.length > 0 && (
          <Link to="/slip" data-testid="print-slip-link" className="label-caps inline-flex items-center gap-2 text-parchment/80 hover:text-oracle border border-white/10 hover:border-oracle/40 rounded-md px-4 py-2.5 transition-colors self-start">
            <Printer className="w-3.5 h-3.5" strokeWidth={1.5} /> Print play slip
          </Link>
        )}
      </div>
      <StreakStats refreshKey={version} />
      <ReferralCard />
      <ResultAlerts />
      {cmp && picks.length > 0 && (
        <div data-testid="latest-draw-compare" className="mb-4 flex flex-wrap items-center gap-3 font-body text-xs text-parchment/60">
          <Trophy className="w-3.5 h-3.5 text-oracle" strokeWidth={1.5} />
          <span>Checked against draw <span className="text-parchment">{cmp.draw.date}</span>:</span>
          <span className="flex items-center gap-1.5">{cmp.draw.whites.map((n) => <PowerballBall key={n} n={n} size="sm" />)}<span className="text-parchment/30">·</span><PowerballBall n={cmp.draw.powerball} red size="sm" /></span>
          <span data-testid="compare-winners" className={cmp.winners ? "text-oracle" : ""}>{cmp.winners ? `${cmp.winners} winning pick${cmp.winners === 1 ? "" : "s"}!` : "No prizes this draw"}</span>
        </div>
      )}
      {loading && <div className="font-body text-parchment/50">Loading…</div>}
      {!loading && picks.length === 0 && (
        <div className="bg-surface border border-white/5 rounded-xl p-8 font-body text-parchment/50 text-center">
          No saved picks yet. Cast a pick from the Oracle above and hit save.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {picks.map((p) => (
          <div key={p.id} data-testid={`saved-pick-${p.id}`} className="bg-surface border border-white/5 rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-caps text-oracle">{p.strategy}</div>
                <div className="font-body text-xs text-parchment/50 mt-1">
                  {new Date(p.created_at).toLocaleString()}
                  {p.target_draw_date && <span data-testid={`pick-target-${p.id}`} className="ml-2 label-caps text-parchment/70 border border-white/10 rounded px-1.5 py-0.5">for {p.target_draw_date}</span>}
                </div>
              </div>
              <button data-testid={`del-pick-${p.id}`} onClick={() => del(p.id)} className="text-parchment/40 hover:text-pb_red transition-colors">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 items-center">
              {p.whites.map((n) => (
                <span key={n} className={resultFor(p.id)?.matched_whites.includes(n) ? "rounded-full ring-2 ring-oracle ring-offset-2 ring-offset-surface" : ""}>
                  <PowerballBall n={n} size="sm" />
                </span>
              ))}
              <span className="text-parchment/30">·</span>
              <span className={resultFor(p.id)?.matched_powerball ? "rounded-full ring-2 ring-oracle ring-offset-2 ring-offset-surface" : ""}>
                <PowerballBall n={p.powerball} red size="sm" />
              </span>
              {resultFor(p.id) && (
                <span data-testid={`pick-result-${p.id}`} className={`ml-2 label-caps ${resultFor(p.id).won ? "text-oracle" : "text-parchment/40"}`}>
                  {resultFor(p.id).won ? `Won ${resultFor(p.id).prize}` : `${resultFor(p.id).matched_whites.length} match${resultFor(p.id).matched_whites.length === 1 ? "" : "es"}${resultFor(p.id).matched_powerball ? " + PB" : ""}`}
                </span>
              )}
            </div>
            <p className="mt-3 font-heading italic text-parchment/70 text-sm leading-snug">&ldquo;{p.reasoning}&rdquo;</p>
          </div>
        ))}
      </div>
    </section>
  );
}
